import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { TsForm } from '@/components/form/Form';
import { SelectInputField } from '@/components/form/SelectInput';
import { Button } from '@/components/shared/button/Button';
import { TSTitleCard } from '@/components/shared/card/Card';
import { ConfirmDialog } from '@/components/shared/modal/ConfirmDialog';
import { TableLoader } from '@/components/shared/table/TableLoader';
import { handleBEFormError } from '@/helpers/beFormErrorHelper';
import { secondsSinceMidnightToTimeString } from '@/helpers/dateHelper';
import { readEmployeeToUpdateEmployee } from '@/helpers/employeeHelper';
import { handleMutationResult } from '@/helpers/toastHelper';
import { Contract } from '@/services/contract/contracts.types';
import { useContracts } from '@/services/contract/useContracts';
import { ReadEmployee, useUpdateStaffMutation } from '@/services/gql/graphql.generated';
import { strings } from '@/services/translation/strings';
import { FC, ReactNode, Suspense, useState } from 'react';
import { Path, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { TSTitleCardStyle } from '../../AddSingleTeamMemberForm';

const ruleContainerClass = 'flex flex-col gap-1';

interface FormProps {
  contractId: string;
}

const ContractField: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <div className={ruleContainerClass}>
    <span className="font-semibold">{label}</span>
    <span>{children ?? '-'}</span>
  </div>
);

const formatTime = (seconds?: number) => (seconds ? secondsSinceMidnightToTimeString(seconds) : '-');

const formatValue = (value?: number) => value ?? '-';

const formatOvertime = (contract?: Contract) =>
  contract ? (contract.overtimeAllowed ? strings.timeshift.common.yes : strings.timeshift.common.no) : '-';

const field = (name: Path<FormProps>) => name;

export const TeamMemberContractEditForm: FC<{ employee: ReadEmployee }> = ({ employee }) => {
  const { contracts } = useContracts();

  const methods = useForm<FormProps>({
    defaultValues: {
      contractId: employee.contract?.contractTypeId
    }
  });

  const navigate = useNavigate();
  const goBack = () => navigate(-1);

  const selectedContractId = useWatch({ control: methods.control, name: 'contractId' });
  const contract = contracts.find(c => c.id === selectedContractId);

  const contractOptions = contracts.map(c => ({ name: c.name ?? '', value: c.id }));

  const [{ fetching }, updateEmployee] = useUpdateStaffMutation();

  const handleSubmit = async (data: FormProps) => {
    const result = await updateEmployee({
      employee: { ...readEmployeeToUpdateEmployee(employee), contractTypeId: data.contractId }
    });

    await handleMutationResult(result, {
      successMessage: 'Team member contract successfully updated.',
      errorMessage: "Unable to update team member's contract.",
      onError: error => handleBEFormError(error, methods),
      onSuccess: goBack
    });
  };

  const s = strings.timeshift.teamMember.contractDetails;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [pendingValues, setPendingValues] = useState<FormProps | null>(null);
  const handleSave = (data: FormProps) => {
    setPendingValues(data);
    setIsModalOpen(true);
  };

  const handleConfirm = async () => {
    if (!pendingValues) return;
    await handleSubmit(pendingValues);
    setIsModalOpen(false);
  };

  return (
    <TsForm methods={methods} onSubmit={handleSave}>
      <TSTitleCard title={strings.timeshift.teamMember.contractDetails.edit} className={TSTitleCardStyle}>
        <SubmissionErrorBoundary>
          <Suspense fallback={<TableLoader />}>
            <div className="grid grid-cols-2 gap-4">
              <SelectInputField
                name={field('contractId')}
                label={s.employeeContract}
                options={contractOptions}
                required
              />

              {/* Empty container for grid layout */}
              <div />

              <ContractField label={s.earliestStartTime}>{formatTime(contract?.earliestStartTime)}</ContractField>

              <ContractField label={s.latestEndTime}>{formatTime(contract?.latestEndTime)}</ContractField>

              <ContractField label={s.minHoursPerWeek}>{formatValue(contract?.minHoursPerWeek)}</ContractField>

              <ContractField label={s.maxHoursPerWeek}>{formatValue(contract?.maxHoursPerWeek)}</ContractField>

              <ContractField label={s.minHoursPerDay}>{formatValue(contract?.minHoursPerDay)}</ContractField>

              <ContractField label={s.maxHoursPerDay}>{formatValue(contract?.maxHoursPerDay)}</ContractField>

              <ContractField label={s.maxDaysPerWeek}>{formatValue(contract?.maxDaysPerWeek)}</ContractField>

              <ContractField label={s.maxConsecutiveDays}>{formatValue(contract?.maxConsecutiveDays)}</ContractField>

              <ContractField label={s.maxShiftHours}>{formatValue(contract?.maxHoursPerShift)}</ContractField>

              <ContractField label={s.allowOvertime}>{formatOvertime(contract)}</ContractField>
            </div>
          </Suspense>
        </SubmissionErrorBoundary>
      </TSTitleCard>

      <ConfirmDialog
        onConfirm={handleConfirm}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={s.confirmationTitle}
        confirmLabel={strings.timeshift.common.confirm}
        cancelLabel={strings.timeshift.common.cancel}
      >
        {s.confirmationMessage}
      </ConfirmDialog>

      <div className="space-y-5 max-w-3xl mx-auto mb-4 pt-5 pl-5">
        <div className="flex w-full items-center justify-end gap-5">
          <Button onClick={goBack}>{strings.common.cancel}</Button>
          <Button primary type="submit" loading={fetching}>
            {strings.common.save}
          </Button>
        </div>
      </div>
    </TsForm>
  );
};
